import { gql as apolloGql } from '@apollo/client';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

type UserRoleIdsRole = {
  id: string;
  name: string;
  description: string | null;
  activeFlag: boolean;
};

export type GetUserRoleIdsQueryVariables = { id: string };
export type GetUserRoleIdsQueryResult = {
  sysUser: {
    id: string;
    userGroupRel: Array<{ id: string; userGroup: UserRoleIdsRole | null }> | null;
  } | null;
};

// Separate from GET_SYS_USER (sys-user.ts) on purpose: `userGroupRel` is gated on
// `userGroup:view`, and the Access tab already depends on that same permission for GET_ROLES
// (to render the pickable role options), so scoping the gated fetch to just this tab means a
// `userGroup:view`-less `user:edit` admin only loses the Access tab's role picker — not the
// rest of the page (Overview/General/Activity), which no longer touches this relation at all.
export const GET_USER_ROLE_IDS: TypedDocumentNode<
  GetUserRoleIdsQueryResult,
  GetUserRoleIdsQueryVariables
> = apolloGql`
  query GetUserRoleIds($id: ID!) {
    sysUser(id: $id) {
      id
      userGroupRel {
        id
        userGroup {
          id
          name
          description
          activeFlag
        }
      }
    }
  }
`;

type UserLocationGroupIdsLocationGroup = {
  id: string;
  name: string;
  activeFlag: boolean;
};

export type GetUserLocationGroupIdsQueryVariables = { id: string };
export type GetUserLocationGroupIdsQueryResult = {
  sysUser: {
    id: string;
    userToLocationGroup: Array<{
      id: string;
      defaultFlag: boolean;
      locationGroup: UserLocationGroupIdsLocationGroup | null;
    }> | null;
  } | null;
};

// Same reasoning as GET_USER_ROLE_IDS above: `userToLocationGroup` is gated on `user:view`
// (fine, same as the rest of this page), but its nested `locationGroup` relation is gated on
// `locationGroup:view` and nullable -- a `locationGroup:view`-less `user:edit` admin only loses
// individual row names in this tab's Location Groups picker, not the whole page.
export const GET_USER_LOCATION_GROUP_IDS: TypedDocumentNode<
  GetUserLocationGroupIdsQueryResult,
  GetUserLocationGroupIdsQueryVariables
> = apolloGql`
  query GetUserLocationGroupIds($id: ID!) {
    sysUser(id: $id) {
      id
      userToLocationGroup {
        id
        defaultFlag
        locationGroup {
          id
          name
          activeFlag
        }
      }
    }
  }
`;

type UserBrandIdsBrand = {
  id: string;
  name: string;
  activeFlag: boolean;
};

export type GetUserBrandIdsQueryVariables = { id: string };
export type GetUserBrandIdsQueryResult = {
  sysUser: {
    id: string;
    brandUser: Array<{
      id: string;
      // The join row's own grant flag, not the Brand's `brand.activeFlag` below.
      activeFlag: boolean;
      brand: UserBrandIdsBrand | null;
    }> | null;
  } | null;
};

// Same reasoning as GET_USER_LOCATION_GROUP_IDS above: `brandUser` is gated on `user:view`
// (fine, same as the rest of this page), but its nested `brand` relation is gated on
// `brand:view`, the same permission the Access tab already needs for GET_BRANDS, so scoping
// the gated fetch to just this tab means a `brand:view`-less `user:edit` admin only loses the
// Access tab's brand picker -- not the rest of the page.
export const GET_USER_BRAND_IDS: TypedDocumentNode<
  GetUserBrandIdsQueryResult,
  GetUserBrandIdsQueryVariables
> = apolloGql`
  query GetUserBrandIds($id: ID!) {
    sysUser(id: $id) {
      id
      brandUser {
        id
        activeFlag
        brand {
          id
          name
          activeFlag
        }
      }
    }
  }
`;

export type GetUserStateIdsQueryVariables = { id: string };
export type GetUserStateIdsQueryResult = {
  sysUser: {
    id: string;
    stateUser: Array<{
      id: string;
      // The join row's own grant flag, not the State's activeFlag -- StateConst has no
      // activeFlag of its own (see GET_STATE_OPTIONS), so there's nothing to confuse this
      // with, but the distinction matters the same way it does for brandUser above.
      activeFlag: boolean;
      stateConst: { id: string } | null;
    }> | null;
  } | null;
};

// Same shape as GET_USER_BRAND_IDS above: `stateUser` is gated on `user:view` (fine, same as
// the rest of this page). Unlike brandUser's nested `brand` relation, `stateConst` carries no
// extra gate of its own (see GET_STATE_OPTIONS -- stateConsts is authenticated: true), so this
// query is ungated end to end past the outer user:view check.
// Only `id` is selected -- selectAssignedStates only ever needs it to build initialStateIds; the
// display-ready name/code come from GET_STATE_OPTIONS instead.
export const GET_USER_STATE_IDS: TypedDocumentNode<
  GetUserStateIdsQueryResult,
  GetUserStateIdsQueryVariables
> = apolloGql`
  query GetUserStateIds($id: ID!) {
    sysUser(id: $id) {
      id
      stateUser {
        id
        activeFlag
        stateConst {
          id
        }
      }
    }
  }
`;

export type UpdateUserInput = {
  departmentId?: string | null;
  roleIds?: string[];
  locationGroupIds?: string[];
  defaultLocationGroupId?: string | null;
  brandIds?: string[];
  stateIds?: string[];
};
export type UpdateUserMutationVariables = { id: string; input: UpdateUserInput };
export type UpdateUserMutationResult = { updateUser: { id: string; departmentId: string | null } };

export const UPDATE_USER = apolloGql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      departmentId
    }
  }
`;
